import { useState, useEffect, useRef } from 'react';
import { Plus, Newspaper } from 'lucide-react';
import { useTickerPrices } from '../../hooks/useTickerPrices';
import { useWatchlistCards } from '../../hooks/useWatchlistCards';
import { useWatchlist } from '../../hooks/useWatchlist';
import { WatchlistCard } from './WatchlistCard';
import AddStockModal from './AddStockModal';

export default function WatchlistSidebar({ view, activeSym, onSelectBrief, onSelectStock }) {
  const { stocks, addStock, removeStock, reorderStocks } = useWatchlist();
  const [addOpen, setAddOpen] = useState(false);
  const { quotes } = useTickerPrices();
  const tickers = stocks.map(s => s.sym);
  const { cards } = useWatchlistCards(tickers);
  const listRef = useRef(null);

  // Sync sb-list scroll with the active main-content scroller.
  // Scrolling starts only once the first .sec-tag in the main content
  // reaches the same vertical level as the sidebar top.
  useEffect(() => {
    const scrollEl = document.querySelector(
      view === 'stock' ? '#stock-scroll' : '.brief-scroll'
    );
    const listEl = listRef.current;
    if (!scrollEl || !listEl) return;

    listEl.scrollTop = 0;

    function sync() {
      const secTag = scrollEl.querySelector('.sec-tag');
      const threshold = secTag ? secTag.offsetTop : Infinity;
      const scrollTop = scrollEl.scrollTop;
      const maxScroll = listEl.scrollHeight - listEl.clientHeight;

      if (maxScroll <= 0) return;

      if (scrollTop > threshold) {
        listEl.scrollTop = Math.min(scrollTop - threshold, maxScroll);
      } else {
        listEl.scrollTop = 0;
      }
    }

    scrollEl.addEventListener('scroll', sync, { passive: true });
    return () => scrollEl.removeEventListener('scroll', sync);
  }, [view, stocks]);

  return (
    <>
    {addOpen && (
      <AddStockModal
        currentSyms={stocks.map(s => s.sym)}
        onAdd={addStock}
        onRemove={removeStock}
        onReorder={reorderStocks}
        onClose={() => setAddOpen(false)}
      />
    )}
    <div className="sidebar-v2">
      <div className="sb-head" onClick={() => setAddOpen(true)} role="button" aria-label="Add stock to watchlist">
        <span className="sb-title">Watchlist</span>
        <span className="sb-add-btn">
          <Plus size={14} />
        </span>
      </div>

      <div className="sb-list" ref={listRef}>
        <div
          className={`sb-brief${view === 'brief' ? ' active' : ''}`}
          onClick={onSelectBrief}
        >
          <Newspaper size={14} /> Daily Brief
        </div>

        {stocks.map(s => {
          const q    = quotes[s.sym];
          const card = cards[s.sym];

          return (
            <WatchlistCard
              key={s.sym}
              ticker={s.sym}
              name={s.name}
              price={q?.price ?? null}
              change={q?.change_pct ?? null}
              todayCall={card?.today_call ?? null}
              projections={card?.accountability ?? null}
              active={s.sym === activeSym && view === 'stock'}
              onClick={() => onSelectStock(s.sym)}
            />
          );
        })}
      </div>
    </div>
    </>
  );
}
